import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faGraduationCap, faSearch, faFilter, faSpinner, faBook, faPlay, faLock, faArrowRight, faExternalLinkAlt } from '@/fontawesome';
import FullscreenError from '@/components/utils/FullscreenError';
import MarkdownRenderer from '@/components/ui/MarkdownRenderer';
import PageBasedPagination from '@/components/ui/PageBasedPagination';
import { handleAPIError } from '@/utils/toasts';
import { formatDate } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import type { Course } from '@/models';
import { useUser } from '@/contexts/UserContext';

const PAGE_SIZE = 50;

interface CourseButtonOverride {
  courseIds: string[];
  buttonText: string;
  buttonLink: string;
  icon: IconDefinition;
  external: boolean;
}

const COURSE_BUTTON_OVERRIDES: CourseButtonOverride[] = [
  {
    courseIds: [
      '68dfea55baa0f8000afd9557', //Trading Boost
      '68fb7dfa277dd0f41197594b', //Daytrading
    ],
    buttonText: 'Trading-Boost Infos',
    buttonLink: 'https://www.svenbolz.com/tradingboost',
    icon: faExternalLinkAlt,
    external: true,
  },
  {
    courseIds: [
      '68c0611b7704f1b0b66523ac', //Ausbildung - Mieteinnahmen mit Aktien
    ],
    buttonText: 'Trading lernen',
    buttonLink: 'https://www.svenbolz.com/tradingausbildung-100k-community',
    icon: faExternalLinkAlt,
    external: true,
  },
  {
    courseIds: [
      '68dfcf3cbaa0f8000afd86cb', //100k Community- Mieteinnahmen mit Aktien
    ],
    buttonText: 'Individuelles Zielplanungsgespräch',
    buttonLink: 'https://www.svenbolz.com/kostenfreies-kennenlerngesprach',
    icon: faExternalLinkAlt,
    external: true,
  },
];

const allOverrideCourseIds = COURSE_BUTTON_OVERRIDES.flatMap(o => o.courseIds);

const findButtonOverride = (courseId: string): CourseButtonOverride | undefined =>
  COURSE_BUTTON_OVERRIDES.find(o => o.courseIds.includes(courseId));

const CoursesView: React.FC = () => {
  const { user } = useUser();
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [criticalError, setCriticalError] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [courseProgress, setCourseProgress] = useState<Record<string, { watched: number; total: number; percent: number }>>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCourses, setTotalCourses] = useState(0);

  useEffect(() => {
    fetchCourses();
  }, [currentPage]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);
      setCriticalError(false);

      const response = await APIRoutes.listCourses({
        published: true,
        limit: PAGE_SIZE,
        next: currentPage > 1 ? `$${currentPage}` : undefined
      });
      setCourses(response.courses);
      setTotalCourses(response.total);

      // Fetch progress for each course
      const progress: Record<string, { watched: number; total: number; percent: number }> = {};
      await Promise.all(
        response.courses.map(async (course) => {
          try {
            const courseProgressResponse = await APIRoutes.getCourseProgress(course.id);
            const videoProgressArray = courseProgressResponse?.videoProgress ?? [];

            const totalVideos = course.videoCount ?? 0;
            const watchedVideos = videoProgressArray.filter(vp => vp.progress >= 0.95).length;

            progress[course.id] = {
              watched: watchedVideos,
              total: totalVideos,
              percent: totalVideos > 0 ? Math.round((watchedVideos / totalVideos) * 100) : 0
            };
          } catch (err) {
            console.error(`Failed to fetch progress for course ${course.id}:`, err);
            progress[course.id] = { watched: 0, total: course.videoCount ?? 0, percent: 0 };
          }
        })
      );

      setCourseProgress(progress);
    } catch (err) {
      console.error('Critical error loading courses:', err);
      setError('Backend nicht erreichbar oder Fehler beim Laden der Kurse');
      setCriticalError(true);
      handleAPIError(err, 'Die Kurse konnten nicht geladen werden. Bitte lade die Seite neu oder versuche es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  const filteredCourses = courses.filter(course =>
    searchQuery === '' ||
    course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    course.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Split courses into accessible and locked
  const accessibleCourses = filteredCourses.filter(course => course.userAccessible === true);
  const lockedCourses = filteredCourses.filter(course => course.userAccessible !== true);
  const visibleLockedCourses = (user?.featureConfig.communityMember ?? false)
    ? lockedCourses.filter(course => allOverrideCourseIds.includes(course.id))
    : lockedCourses;

  // Show fullscreen error if critical error occurred
  if (criticalError) {
    return (
      <FullscreenError
        title="Kurse nicht verfügbar"
        message={error || 'Die Kurse konnten nicht geladen werden. Möglicherweise ist das Backend nicht erreichbar.'}
        onRetry={fetchCourses}
      />
    );
  }

  return (
    <>
      <div>
        <div className="text-center mb-12">
          <FontAwesomeIcon
            icon={faGraduationCap}
            className="w-16 h-16 text-blue-600 dark:text-blue-400 mb-4 mx-auto"
          />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Trading Kurse
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Entdecken Sie unsere umfassenden Trading-Kurse und erweitern Sie Ihr Wissen
            mit professionellen Video-Lektionen und praktischen Übungen.
          </p>
        </div>

        {/* Search and Filter Controls */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                placeholder="Kurse durchsuchen..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                <FontAwesomeIcon icon={faFilter} className="mr-2" />
                {filteredCourses.length} von {courses.length} Kursen
              </div>
            </div>
          </div>
        </div>

        {loading && (
          <div className="flex justify-center items-center py-12">
            <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
          </div>
        )}

        {error && !criticalError && (
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        {!loading && filteredCourses.length === 0 && searchQuery && (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faSearch} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Keine Kurse gefunden
            </h3>
            <p className="text-gray-500 dark:text-gray-500">
              Versuchen Sie eine andere Suche oder entfernen Sie Filter.
            </p>
          </div>
        )}

        {!loading && filteredCourses.length === 0 && !searchQuery && (
          <div className="text-center py-12">
            <FontAwesomeIcon icon={faGraduationCap} className="w-16 h-16 text-gray-400 dark:text-gray-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-2">
              Keine Kurse verfügbar
            </h3>
            <p className="text-gray-500 dark:text-gray-500">
              Momentan sind keine Kurse veröffentlicht. Schauen Sie später wieder vorbei.
            </p>
          </div>
        )}

        {/* Accessible Courses Section */}
        {!loading && accessibleCourses.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              Meine Kurse
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {accessibleCourses.map((course) => {
                const progress = courseProgress[course.id] ?? { watched: 0, total: 0, percent: 0 };
                return (
                <div key={course.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
                  {course.thumbnail && (
                    <Link
                      to={`/courses/${course.id}`}
                      className="block aspect-video relative overflow-hidden cursor-pointer"
                    >
                      <img
                        src={APIRoutes.getBlobContentUrl(course.thumbnail)}
                        alt={course.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                      {course.free && (
                        <div className="absolute top-3 right-3 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                          KOSTENLOS
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-300">
                        <FontAwesomeIcon
                          icon={faPlay}
                          className="w-12 h-12 text-white/90"
                        />
                      </div>
                    </Link>
                  )}

                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                            {course.title}
                          </h3>
                        </div>
                      </div>

                      <div className="mb-6 line-clamp-5">
                        <MarkdownRenderer className="text-sm">
                          {course.description}
                        </MarkdownRenderer>
                      </div>

                      <div className="flex items-center justify-between mb-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faBook} className="w-4 h-4 mr-1" />
                            <span>{course.moduleCount} Module</span>
                          </div>
                          <div>
                            <span>{course.videoCount} Videos</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Fortschritt</span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">{progress.percent}%</span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-full transition-all duration-500 ${
                            progress.percent === 100
                              ? 'bg-green-500'
                              : 'bg-blue-600'
                          }`}
                          style={{ width: `${progress.percent}%` }}
                        />
                      </div>
                      {progress.total > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {progress.watched} von {progress.total} Videos angesehen
                        </div>
                      )}
                    </div>

                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                      <Link
                        to={`/courses/${course.id}`}
                        className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 cursor-pointer"
                      >
                        <FontAwesomeIcon icon={faPlay} className="w-4 h-4" />
                        <span>Kurs starten</span>
                      </Link>

                      <div className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
                        Erstellt: {formatDate(course.created)}
                      </div>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Divider between sections */}
        {!loading && accessibleCourses.length > 0 && visibleLockedCourses.length > 0 && (
          <hr className="border-gray-300 dark:border-gray-600 my-12" />
        )}

        {/* Locked Courses Section */}
        {!loading && visibleLockedCourses.length > 0 && (
          <div>
            <div className="flex items-center mb-6">
              <FontAwesomeIcon icon={faLock} className="w-6 h-6 text-gray-400 dark:text-gray-500 mr-3" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Weitere Kurse freischalten
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {visibleLockedCourses.map((course) => (
                <div key={course.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-xl transition-shadow duration-300 flex flex-col h-full">
                  {course.thumbnail && (
                    <Link
                      to={`/courses/${course.id}`}
                      className="block aspect-video relative overflow-hidden cursor-pointer"
                    >
                      <img
                        src={APIRoutes.getBlobContentUrl(course.thumbnail)}
                        alt={course.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                      {course.free && (
                        <div className="absolute top-3 right-3 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                          KOSTENLOS
                        </div>
                      )}
                      {/* Lock overlay */}
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                        <FontAwesomeIcon
                          icon={faLock}
                          className="w-12 h-12 text-white/80"
                        />
                      </div>
                    </Link>
                  )}

                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 line-clamp-2">
                            {course.title}
                          </h3>
                        </div>
                      </div>

                      <div className="mb-6 line-clamp-5">
                        <MarkdownRenderer className="text-sm">
                          {course.description}
                        </MarkdownRenderer>
                      </div>

                      <div className="flex items-center justify-between mb-4 text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center">
                            <FontAwesomeIcon icon={faBook} className="w-4 h-4 mr-1" />
                            <span>{course.moduleCount} Module</span>
                          </div>
                          <div>
                            <span>{course.videoCount} Videos</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                      <Link
                        to={`/courses/${course.id}`}
                        className="w-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 cursor-pointer"
                      >
                        <FontAwesomeIcon icon={faPlay} className="w-4 h-4" />
                        <span>Kursinhalt ansehen</span>
                      </Link>
                      {(() => {
                        const override = findButtonOverride(course.id);
                        if (override !== undefined) {
                          return override.external ? (
                            <a
                              href={override.buttonLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 cursor-pointer"
                            >
                              <FontAwesomeIcon icon={override.icon} className="w-4 h-4" />
                              <span>{override.buttonText}</span>
                            </a>
                          ) : (
                            <Link
                              to={override.buttonLink}
                              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 cursor-pointer"
                            >
                              <FontAwesomeIcon icon={override.icon} className="w-4 h-4" />
                              <span>{override.buttonText}</span>
                            </Link>
                          );
                        }
                        return (
                          <Link
                            to="/pricing"
                            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2 cursor-pointer"
                          >
                            <FontAwesomeIcon icon={faArrowRight} className="w-4 h-4" />
                            <span>Zum Upgrade-Bereich</span>
                          </Link>
                        );
                      })()}

                      <div className="mt-3 text-center text-xs text-gray-500 dark:text-gray-400">
                        Erstellt: {formatDate(course.created)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <PageBasedPagination
          currentPage={currentPage}
          totalCount={totalCourses}
          pageSize={PAGE_SIZE}
          onPageChange={setCurrentPage}
          loading={loading}
        />
      </div>
    </>
  );
};

export default CoursesView;
